import { DummySettingsAutoSelect } from 'src/v2/components/LeftSidePanel/components/DummySettingsAutoSelect';
import {
  useActiveModel,
  useBreakpoint,
  useSkillCommand,
  useUserMetadata,
  useUserSettings,
} from 'src/hooks';
import { getModelDisplayName, getModelIcon } from 'src/utils';
import {
  NINJA_AGENT_TEXT_AUTO_MODEL,
  SUPER_AGENT_AS_EXTERNAL_MODEL,
  SUPER_AGENT_DEEP_RESEARCH_MODEL,
} from 'src/constants';
import {
  QuickSkillCommand,
  SettingKeys,
  SingleSelectSetting,
  TaskSkill,
} from 'src/types';
import { SettingsSelect } from 'src/v2/components/LeftSidePanel/components/SettingsSelect';
import { Sliders } from '@phosphor-icons/react';
import { BetaLabel } from 'src/v2/commonComponents/BetaLabel';
import styles from './SkillModelSelector.module.scss';

type Supported =
  | QuickSkillCommand.WRITER
  | QuickSkillCommand.IMAGE
  | QuickSkillCommand.RESEARCHER
  | QuickSkillCommand.DEEP_RESEARCHER
  | QuickSkillCommand.THINKING
  | QuickSkillCommand.VIDEO;

export const SkillModelSelector = () => {
  const { activeSkillCommand } = useSkillCommand();
  const { metadata } = useUserMetadata();
  const { isDesktopAndUp } = useBreakpoint();
  const { activeModel, activeInputBoxModel, changeInputBoxActiveModel } =
    useActiveModel();

  const { setting: writerSetting } = useUserSettings<SingleSelectSetting>(
    SettingKeys.WRITER_CODER_PRIMARY_MODEL,
  );

  const { setting: researcherSetting } = useUserSettings<SingleSelectSetting>(
    SettingKeys.RESEARCH_PRIMARY_MODEL,
  );

  const shouldReadFromSettings = !activeModel;

  const researcherModelFromTop =
    activeModel && activeModel.includes(SUPER_AGENT_AS_EXTERNAL_MODEL)
      ? NINJA_AGENT_TEXT_AUTO_MODEL
      : activeModel;

  const handleWriterModelChange = (value: string) => {
    changeInputBoxActiveModel(value);
  };

  const handleResearcherModelChange = (value: string) => {
    changeInputBoxActiveModel(value);
  };

  const data: Record<Supported, JSX.Element> = {
    [QuickSkillCommand.WRITER]: (
      <SettingsSelect
        settingKey={SettingKeys.WRITER_CODER_PRIMARY_MODEL}
        skill={TaskSkill.CHITCHAT}
        value={
          shouldReadFromSettings
            ? writerSetting?.value
            : activeInputBoxModel || activeModel
        }
        onChange={shouldReadFromSettings ? undefined : handleWriterModelChange}
      />
    ),

    [QuickSkillCommand.IMAGE]: (
      <SettingsSelect
        settingKey={SettingKeys.IMAGE_PRIMARY_MODEL}
        skill={TaskSkill.IMAGE}
      />
    ),

    [QuickSkillCommand.RESEARCHER]: (
      <SettingsSelect
        settingKey={SettingKeys.RESEARCH_PRIMARY_MODEL}
        skill={TaskSkill.RESEARCH}
        value={
          shouldReadFromSettings
            ? researcherSetting?.value
            : activeInputBoxModel || researcherModelFromTop
        }
        onChange={
          shouldReadFromSettings ? undefined : handleResearcherModelChange
        }
      />
    ),

    [QuickSkillCommand.DEEP_RESEARCHER]: (
      <DummySettingsAutoSelect
        skill={TaskSkill.DEEP_RESEARCH}
        iconName={getModelIcon({
          targetModel: SUPER_AGENT_DEEP_RESEARCH_MODEL,
          metadata,
        })}
        title={getModelDisplayName({
          targetModel: SUPER_AGENT_DEEP_RESEARCH_MODEL,
          metadata,
        })}
      />
    ),

    [QuickSkillCommand.VIDEO]: (
      <>
        <DummySettingsAutoSelect
          title="Amazon Nova Reel"
          skill={TaskSkill.VIDEO}
          iconName="nova"
        />
        <DummySettingsAutoSelect
          toggleTitle={isDesktopAndUp ? 'Duration: 6 seconds' : ''}
          title="6 seconds"
          IconComponent={Sliders}
          labelComponent={<BetaLabel className={styles.betaLabel} />}
        />
      </>
    ),
    [QuickSkillCommand.THINKING]: (
      <DummySettingsAutoSelect
        title="Reasoning 2.0"
        iconName="ninjaPixProLogo"
      />
    ),
  };

  return activeSkillCommand && activeSkillCommand in data
    ? data[activeSkillCommand as Supported]
    : null;
};
